import React, { useState, useEffect } from 'react';
import { Link, Copy, UserPlus, Calendar, Mail, ExternalLink, X } from 'lucide-react';
import axios from 'axios';

import { BACKEND_URL } from "../utils/apiBase";
const API = `${BACKEND_URL}/api`;

const InviteClient = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [invitations, setInvitations] = useState([]);
  const [generatedInvite, setGeneratedInvite] = useState(null);
  const [showTooltip, setShowTooltip] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    loadInvitations();
  }, []);

  const loadInvitations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/pro/invitations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvitations(response.data.invitations);
    } catch (error) {
      console.error('Failed to load invitations:', error);
      setError('Failed to load invitations');
    }
  };

  const handleGenerateInvite = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/pro/invitations`, 
        { email: email || undefined },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setGeneratedInvite(response.data);
      setEmail('');
      await loadInvitations(); // Refresh the list
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to generate invite');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyLink = async (inviteUrl, inviteId) => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setShowTooltip(inviteId);
      setTimeout(() => setShowTooltip(''), 2000);
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = inviteUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setShowTooltip(inviteId);
      setTimeout(() => setShowTooltip(''), 2000);
    }
  };

  const handleRevokeInvite = async (inviteId) => {
    if (!window.confirm('Are you sure you want to revoke this invitation?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.patch(`${API}/pro/invitations/${inviteId}`, 
        { status: 'revoked' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await loadInvitations(); // Refresh the list
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to revoke invitation');
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      accepted: 'bg-green-100 text-green-800',
      revoked: 'bg-red-100 text-red-800',
      expired: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || colors.pending;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Invite Clients</h1>
        <Link 
          to="/professional/clients"
          className="inline-flex items-center px-4 py-2 text-sm font-medium text-blue-600 bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors"
        >
          View All Clients
          <ExternalLink className="w-4 h-4 ml-2" />
        </Link>
      </div>

      {/* Generate New Invite */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Generate New Invitation</h2>
        
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {generatedInvite && (
          <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-green-800 mb-2">Invitation Generated!</h3>
            <div className="flex items-center space-x-3">
              <input
                type="text"
                value={generatedInvite.invite_url}
                readOnly
                className="flex-1 px-3 py-2 border border-green-300 rounded-md bg-white text-sm"
              />
              <button
                onClick={() => handleCopyLink(generatedInvite.invite_url, generatedInvite.invite_id)}
                className="relative inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 transition-colors"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy Link
                {showTooltip === generatedInvite.invite_id && (
                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-2 py-1 bg-gray-800 text-white text-xs rounded-md whitespace-nowrap z-10">
                    Link copied!
                  </div>
                )}
              </button>
            </div>
            <p className="text-sm text-green-700 mt-2">
              Share this link with your client. They can register using this invitation.
            </p>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Client Email (Optional)
            </label>
            <div className="flex space-x-3">
              <div className="flex-1 relative">
                <Mail className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter client's email address (optional)"
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <button
                onClick={handleGenerateInvite}
                disabled={isLoading}
                className={`inline-flex items-center px-6 py-2 border border-transparent text-sm font-medium rounded-md text-white ${
                  isLoading
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-blue-600 hover:bg-blue-700'
                } transition-colors`}
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Generating...
                  </>
                ) : (
                  <>
                    <UserPlus className="w-4 h-4 mr-2" />
                    Generate Invite
                  </>
                )}
              </button>
            </div>
            <p className="text-sm text-gray-500 mt-2">
              Email is optional. You can generate a generic invitation link to share manually.
            </p>
          </div>
        </div>
      </div>

      {/* Pending Invitations */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Pending Invitations</h2>
        </div>
        
        {invitations.length === 0 ? (
          <div className="px-6 py-8 text-center">
            <UserPlus className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No invitations yet</h3>
            <p className="text-gray-500">
              Generate your first invitation to start onboarding clients.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email / Link
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {invitations.map((invitation) => (
                  <tr key={invitation.invite_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        {invitation.email ? (
                          <div className="text-sm font-medium text-gray-900">{invitation.email}</div>
                        ) : (
                          <div className="text-sm text-gray-500">Generic invitation</div>
                        )}
                        <div className="text-xs text-gray-400">
                          ID: {invitation.invite_id.substring(0, 8)}...
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(invitation.status)}`}>
                        {invitation.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 mr-1" />
                        {formatDate(invitation.created_at)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      {invitation.status === 'pending' && (
                        <>
                          <button
                            onClick={() => handleCopyLink(
                              `${window.location.origin}/register?invite=${invitation.invite_id}`,
                              invitation.invite_id
                            )}
                            className="relative inline-flex items-center text-blue-600 hover:text-blue-900 transition-colors"
                          >
                            <Copy className="w-4 h-4 mr-1" />
                            Copy
                            {showTooltip === invitation.invite_id && (
                              <div className="absolute top-full left-0 mt-1 px-2 py-1 bg-gray-800 text-white text-xs rounded-md whitespace-nowrap z-10">
                                Link copied!
                              </div>
                            )}
                          </button>
                          <button
                            onClick={() => handleRevokeInvite(invitation.invite_id)}
                            className="inline-flex items-center text-red-600 hover:text-red-900 transition-colors"
                          >
                            <X className="w-4 h-4 mr-1" />
                            Revoke
                          </button>
                        </>
                      )}
                      {invitation.status !== 'pending' && (
                        <span className="text-gray-400">No actions available</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default InviteClient;